import { FunctionComponent, useRef } from "react";
import styles from "./Snackbar.module.css";
import {
  LabelPairedXmarkSmRegularIcon,
  StandaloneCircleInfoBoldIcon,
  StandaloneTriangleExclamationBoldIcon,
} from "@deriv/quill-icons";

export type SnackbarType = {
  className?: string;
  message?: string;
  showButtonAction?: boolean;
  showButtonClose?: boolean;
  showIcon?: boolean;

  /** Custom props */
  actionLabel?: string; // New prop for customizable action button label
  onActionClick?: () => void; // New prop for action button click handler
  onCloseClick?: () => void; // New prop for close button click handler

  /** Variant props */
  indicator?: "neutral" | "fail";
  type?: "mobile" | "desktop";

  /** Auto-dismiss props */
  autoDismiss?: boolean; // Whether to auto-dismiss the snackbar
  dismissTimeout?: number; // Time in ms before auto-dismissing

  /** Internal props */
  id?: string; // Used internally by the controller
};

export const Snackbar: FunctionComponent<SnackbarType> = ({
  className = "",
  indicator = "neutral",
  type = "mobile",
  message = "Message goes here",
  showButtonAction = true,
  showButtonClose = true,
  showIcon = true,
  actionLabel = "Action",
  onActionClick,
  onCloseClick,
}) => {
  const SnackbarId = `quill-snackbar-${indicator}-${type}`;

  const defaultIcons = {
    neutral: StandaloneCircleInfoBoldIcon,
    fail: StandaloneTriangleExclamationBoldIcon,
  } as const;

  const IconComponent = defaultIcons[indicator] as React.ComponentType;

  return (
    <div
      className={[styles.root, className].join(" ")}
      data-indicator={indicator}
      data-type={type}
      data-id={SnackbarId}
    >
      {showIcon && (
        <div className={`${styles.icon1} snackbar-icon`}>
          {/* @ts-ignore */}
          <IconComponent iconSize="sm" />
        </div>
      )}
      <div className={styles.containerMessage}>
        <div className={styles.message}>{message}</div>
      </div>
      {showButtonAction && (
        <div className={styles.buttonAction}>
          <div
            className={styles.label}
            onClick={onActionClick}
            style={{
              cursor: "pointer",
            }}
          >
            {actionLabel}
          </div>
          <div className={styles.bgFocus} />
        </div>
      )}
      {showButtonClose && (
        <div
          className={styles.buttonClose}
          onClick={onCloseClick}
          style={{ cursor: "pointer" }}
        >
          <div className={`${styles.icon6} snackbar-icon`}>
            <LabelPairedXmarkSmRegularIcon />
          </div>
          <div className={styles.bgFocus1} />
        </div>
      )}
    </div>
  );
};

export { SnackbarController } from "./SnackbarController";
export {
  useSnackbar,
  showSnackbar,
  hideSnackbar,
} from "../../hooks/useSnackbar";

export default Snackbar;
